import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { NITI_STORY_URL } from "../components/NitiFeature";
import coverImage from "../assets/mou/mou emrss.jpeg";
import "./NitiAayogFeature.scss";
import { nitiFigures } from "../data/stats";

const CANONICAL = "https://cograd.in/blog/cograd-featured-by-niti-aayog/";
// Served from /public so crawlers and social scrapers get a stable, absolute URL.
const OG_IMAGE = "https://cograd.in/og-niti-aayog-feature.jpg";
const PUBLISHED = "2026-08-31";

const SEO_TITLE =
  "CoGrad Featured by NITI Aayog for AI-Powered Classroom Innovation";
const SEO_DESCRIPTION =
  "CoGrad's Medha AI has been featured by NITI Aayog's Frontier Technologies platform for helping teachers reduce classroom preparation time by 30–40%.";

const HEADLINE =
  "CoGrad Featured by NITI Aayog for Its AI-Powered Approach to Transforming Classrooms";

// Every figure below is stated in the NITI Aayog feature. Nothing is added.
const FACTS = [
  { figure: "30–40%", label: "less classroom preparation time" },
  { figure: "10,000", label: "teachers using Medha AI" },
  { figure: "~2,00,000", label: "schools reached" },
  { figure: "87%", label: "students improved within 60 days" },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  headline: HEADLINE,
  description: SEO_DESCRIPTION,
  image: [OG_IMAGE],
  datePublished: PUBLISHED,
  dateModified: PUBLISHED,
  inLanguage: "en-IN",
  mainEntityOfPage: { "@type": "WebPage", "@id": CANONICAL },
  author: { "@type": "Organization", name: "CoGrad", url: "https://cograd.in/" },
  publisher: {
    "@type": "Organization",
    name: "CoGrad",
    url: "https://cograd.in/",
    logo: {
      "@type": "ImageObject",
      url: "https://cograd.in/logo512.png",
    },
  },
  about: [
    { "@type": "Thing", name: "Medha AI" },
    { "@type": "Thing", name: "AI in education" },
  ],
  citation: {
    "@type": "CreativeWork",
    name: "Using AI, This Digital Platform Reduces Classroom Prep Time by 30–40% for Teachers",
    url: NITI_STORY_URL,
    publisher: { "@type": "Organization", name: "NITI Aayog Frontier Tech Hub" },
  },
};

export default function NitiAayogFeature() {
  // Deep links land at the top of the article, not mid-page.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={CANONICAL} />

        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="CoGrad" />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="article:published_time" content={PUBLISHED} />
        <meta property="article:section" content="Recognition" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />

        <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
      </Helmet>

      <Header />

      <article className="na">
        {/* ══ Masthead ═══════════════════════════════════════════════════════ */}
        <header className="na__head">
          <div className="na__wrap">
            <nav className="na__crumbs" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span aria-hidden="true">/</span>
              <span>Recognition</span>
            </nav>

            <p className="na__eyebrow">Featured by NITI Aayog</p>

            <h1 className="na__h1">{HEADLINE}</h1>

            <p className="na__standfirst">
              NITI Aayog&rsquo;s Frontier Tech Hub has profiled Medha AI, CoGrad&rsquo;s
              classroom platform for teachers, in a feature on how generative AI is
              cutting the hours teachers spend preparing for class.
            </p>

            <div className="na__byline">
              <span>CoGrad Newsroom</span>
              <span className="na__byline-dot" aria-hidden="true" />
              <time dateTime={PUBLISHED}>31 August 2026</time>
              <span className="na__byline-dot" aria-hidden="true" />
              <span>5 min read</span>
            </div>
          </div>
        </header>

        {/* ══ Cover ══════════════════════════════════════════════════════════ */}
        <figure className="na__cover">
          <div className="na__wrap na__wrap--wide">
            <img
              src={coverImage}
              alt="CoGrad representatives at the Eklavya Model Residential Schools MoU signing with the Ministry of Tribal Affairs"
              width="1600"
              height="1067"
            />
            <figcaption>
              CoGrad at the Eklavya Model Residential Schools MoU signing with the
              Ministry of Tribal Affairs &mdash; the photograph carried on the NITI
              Aayog Frontier Tech Hub feature. <em>Photo: CoGrad</em>
            </figcaption>
          </div>
        </figure>

        {/* ══ Body ═══════════════════════════════════════════════════════════ */}
        <div className="na__wrap na__body">
          <p className="na__lead">
            Ask most school teachers where their week goes and the answer is rarely
            &ldquo;teaching&rdquo;. Lesson plans, worksheets, assessments, records
            &mdash; the work that surrounds a class often crowds out the class
            itself. That is the problem Medha AI was built for, and it is the reason
            CoGrad now appears on one of the country&rsquo;s more closely watched
            technology showcases.
          </p>

          <h2>A national platform takes note</h2>
          <p>
            NITI Aayog&rsquo;s Frontier Tech Hub documents Indian innovations working
            with emerging technologies, and its Frontier Voices series profiles the
            teams behind them. In a feature updated on 31 August 2026, the platform
            examined how CoGrad is applying generative AI inside government and
            aided school classrooms, filed under Education, with CoGrad listed as a
            Uttar Pradesh innovation.
          </p>
          <p>
            The framing matters. This is editorial coverage on a government
            innovation platform &mdash; an independent look at what the technology
            does and what it has changed for the teachers using it, rather than a
            certification or an endorsement.
          </p>

          <h2>What the feature highlighted</h2>
          <p>
            The story opens on a structural problem: India has a large and growing
            school-age population, but quality teaching is thinly spread outside big
            cities, held back by shortages of both teachers and teaching material.
            Online alternatives, it notes, tend to assume reliable connectivity and
            English fluency &mdash; two things a rural classroom often cannot count
            on. Few platforms are built for the context they are actually used in.
          </p>
          <p>
            Founded in 2019 by Himanshu Chaurasia and Saurabh Yadav and headquartered
            in Prayagraj, CoGrad set out to build for that context: a multilingual
            classroom management platform designed to run where bandwidth is scarce.
          </p>

          <h2>The classroom preparation problem</h2>
          <p>
            Preparation is invisible work. A single period can carry a lesson plan, a
            supporting activity, a worksheet and an assessment behind it, multiplied
            across subjects and sections. It is the part of teaching that follows a
            teacher home, and the part that gets thinner as workload rises.
          </p>

          <h2>How Medha AI helps teachers</h2>
          <p>
            Medha AI&rsquo;s core job, as the feature describes it, is content
            creation. It uses generative AI to assemble lesson plans, classroom
            activities, assessments and worksheets across school subjects &mdash;
            including newer areas such as coding and robotics, where teachers are
            often introducing a topic they are meeting for the first time themselves.
          </p>
          <p>
            What comes back arrives in plain dashboards rather than analytics
            dense enough to need a specialist. With basic training, the feature notes,
            teachers can read that information and fold it into how they teach. The
            platform is built to work in low-connectivity regions, which is what makes
            it usable in the low-income rural schools it was designed around.
          </p>
          <p>
            CoGrad developed Medha AI with funding from the Ministry of Electronics
            and Information Technology under the TIDE 2.0 scheme, which supported
            research and development, piloting and scaling.
          </p>

          {/* Statistic anchor — the single most quotable number in the story. */}
          <aside className="na__pull">
            <span className="na__pull-figure">30&ndash;40%</span>
            <span className="na__pull-label">
              reduction in class preparation time reported by teachers after Medha AI
              took on lesson planning, assessments and supplementary tasks
            </span>
          </aside>

          <h2>What changed for teachers &mdash; and students</h2>
          <p>
            The headline result in the feature is that preparation time fell by 30 to
            40% for teachers using the platform, with Medha AI absorbing lesson
            planning, assessments and the supplementary work around them. Hours
            returned to teachers are hours available for the classroom, and for the
            kind of individual attention that a heavy administrative load quietly
            eliminates.
          </p>
          <p>
            The effect showed up on the students&rsquo; side as well. In an initial
            deployment, the feature reports, 87% of participating students showed
            improved academic performance within 60 days.
          </p>

          <h2>Where Medha AI is being used</h2>
          <p>
            The platform has been deployed across Navodaya Vidyalaya Samitis, Kendriya
            Vidyalaya Sangathans, Eklavya Model Residential Schools and Delhi&rsquo;s
            CM SHRI schools. Across states and union territories, the feature puts its
            reach at roughly 10,000 teachers and about 2,00,000 schools.
          </p>

          <dl className="na__facts">
            {FACTS.map((f) => (
              <div className="na__facts-item" key={f.label}>
                <dt>{f.figure}</dt>
                <dd>{f.label}</dd>
              </div>
            ))}
          </dl>
          <p className="na__facts-note">
            All figures as reported in the NITI Aayog Frontier Tech Hub feature,
            updated {nitiFigures.updated}. They count what that feature counts,
            which is not the same as the partner-school total reported
            elsewhere on this site.
          </p>

          <h2>Recognition so far</h2>
          <p>
            The feature also records CoGrad&rsquo;s appearance on Shark Tank India in
            2025, where the company secured investment and wider visibility for its
            work in education, and its recognition at the Indian Education Awards in
            2026.
          </p>

          <h2>What comes next</h2>
          <p>
            Looking ahead, the feature argues that Medha AI&rsquo;s momentum comes
            from being embedded in institutions rather than sold school by school, and
            that public-sector programmes could anchor it in long-term teaching
            frameworks. It points to bodies such as SCERTs and DIETs as natural users
            &mdash; for teacher recruitment and skill development, and for the impact
            assessment and content validation work that would test the technology
            properly. Clear national guidance on how AI tools should be used, it
            suggests, would raise accountability across the field and help build
            public trust.
          </p>

          <h2>Why this matters to us</h2>
          <p>
            Being written about on a national innovation platform does not change what
            happens in a classroom in Prayagraj or Shajapur. What it does is put an
            independent lens on it. The measure CoGrad is judged by here is not
            features shipped or schools signed &mdash; it is whether a teacher got
            time back, and whether students were better off for it.
          </p>
          <p>
            <a
              className="na__inline-link"
              href={NITI_STORY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read the full feature on NITI Aayog&rsquo;s Frontier Technologies
              platform &rarr;
            </a>
          </p>

          {/* ══ Outbound CTA ═════════════════════════════════════════════════ */}
          <aside className="na__cta-card">
            <div className="na__cta-source">
              <b>NITI Aayog</b>
              <i>Frontier Technologies</i>
            </div>
            <p className="na__cta-headline">
              Using AI, This Digital Platform Reduces Classroom Prep Time by
              30&ndash;40% for Teachers
            </p>
            <a
              className="na__cta-btn"
              href={NITI_STORY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read the NITI Aayog Story
              <ArrowUpRight size={17} strokeWidth={2.4} />
            </a>
            <p className="na__cta-host">frontiertech.niti.gov.in</p>
          </aside>

          <p className="na__disclaimer">
            CoGrad was featured in an independent editorial story on NITI Aayog&rsquo;s
            Frontier Tech Hub. The feature does not constitute a partnership,
            endorsement, certification or approval by NITI Aayog or the Government of
            India. All figures cited on this page are drawn from that feature.
          </p>

          <div className="na__more">
            <a href="/">&larr; Back to cograd.in</a>
            <a href="https://medha.cograd.in/" target="_blank" rel="noopener noreferrer">
              Explore Medha AI &rarr;
            </a>
          </div>
        </div>
      </article>

      <Footer />
    </>
  );
}
